import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { getCargoOperationsAfter } from '../stowageRotationUtils';
import { isLoadingOrUnloadingCargoOperation } from '../cargoOperationUtils';
import { removeAdjustedCargoTemperaturesWithoutStowage } from './adjustedCargoTemperatureUtils';
import { createDeleteStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { UUID } from 'crypto';
import { removeSubsequentUnloadingStowageItems } from './autoUnstowUtils';

const logUndoStowage: boolean = false;
/**
 * Utility function for logging operations
 */
const logOperations = (operations: RotationCargoOperation[], message?: string): void => {
    if (!logUndoStowage) {
        return;
    }

    if (message) {
        console.log(`${message} ${operations.length}`);
    }

    operations.forEach((operation) => {
        console.log(`${operation.operationType} #${operation.cargo.cargoNumber} ${operation.nominatedWeight} MT`);
    });
};

/**
 * Check if stowage can be undone for a cargo operation
 */
export const canUndoStowage = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): boolean => {
    if (logUndoStowage)
        console.log('Checking if stowage can be undone', {
            stowagePlanId: stowagePlan?.stowagePlanGuid,
            cargoOperationId: cargoOperation?.cargoOperationGuid,
        });

    const result =
        stowagePlan &&
        cargoOperation &&
        isLoadingOrUnloadingCargoOperation(cargoOperation) &&
        stowagePlan.stowageItems.some((s) => s.cargoOperationGuid === cargoOperation.cargoOperationGuid);

    if (logUndoStowage) console.log(`Can undo stowage: ${result}`);
    return result;
};

/**
 * Creates a delete stowage change and logs it
 */
const createAndLogDeleteStowageChange = (
    stowagePlanGuid: UUID,
    itemGuid: UUID,
    changeType: StowageChangeDataType,
    logMessage: string,
    contextData: Record<string, any>
): StowagePlanChange => {
    console.log(logMessage, contextData);
    return createDeleteStowagePlanChange(stowagePlanGuid, itemGuid, changeType);
};

/**
 * Helper to filter stowage items and generate plan changes
 */
const filterStowageItemsAndCreateChanges = (
    stowageItems: any[],
    stowagePlanGuid: UUID,
    filterFn: (item: any) => boolean,
    logMessage: string,
    getContextData: (item: any) => Record<string, any>
): { filteredItems: any[]; changes: StowagePlanChange[]; removedItems: any[] } => {
    const changes: StowagePlanChange[] = [];
    const removedItems: any[] = [];
    const initialCount = stowageItems.length;

    const filteredItems = stowageItems.reduce((acc, item) => {
        if (!filterFn(item)) {
            acc.push(item);
        } else {
            changes.push(
                createAndLogDeleteStowageChange(
                    stowagePlanGuid,
                    item.stowageGuid,
                    StowageChangeDataType.StowageItem,
                    logMessage,
                    getContextData(item)
                )
            );
            removedItems.push(item);
        }
        return acc;
    }, []);

    if (logUndoStowage) console.log(`Removed ${initialCount - filteredItems.length} stowage items`);
    return { filteredItems, changes, removedItems };
};

/**
 * Applies temperature adjustments cleanup and returns changes
 */
const cleanupTemperatureAdjustments = (stowagePlan: StowagePlan): StowagePlanChange[] => {
    const temperatureChanges = removeAdjustedCargoTemperaturesWithoutStowage(stowagePlan);
    if (logUndoStowage) console.log(`Removed ${temperatureChanges.length} adjusted cargo temperatures without stowage`);
    return temperatureChanges;
};

/**
 * Shared implementation for undoing stowage operations
 * @param stowagePlan The stowage plan to modify
 * @param primaryFilterFn Function to filter stowage items for the primary operation
 * @param transferOperationFilterFn Function to determine if a transfer operation should be deleted
 * @param logContext Context information for logging
 * @param tankId Optional tank ID to constrain subsequent operations to a specific tank
 */
const undoStowageImplementation = (
    stowagePlan: StowagePlan,
    primaryFilterFn: (item: any) => boolean,
    transferOperationFilterFn: (op: RotationCargoOperation, cargoGuid: UUID, deletedItems: any[]) => boolean,
    logContext: Record<string, any>,
    tankId?: number
): StowagePlanChange[] => {
    if (logUndoStowage) console.log('Undoing stowage with implementation', logContext);

    let stowagePlanChanges: StowagePlanChange[] = [];

    // Step 1: Remove stowage items directly related to this operation
    const primaryResult = filterStowageItemsAndCreateChanges(
        stowagePlan.stowageItems,
        stowagePlan.stowagePlanGuid,
        primaryFilterFn,
        'Step 1: Deleting stowage item',
        (item) => ({ stowageGuid: item.stowageGuid })
    );

    stowagePlan.stowageItems = primaryResult.filteredItems;
    stowagePlanChanges = stowagePlanChanges.concat(primaryResult.changes);
    const removedCargoOperations = primaryResult.removedItems.map((item) => item.cargoOperation);
    const cargoOperation = removedCargoOperations.length > 0 ? removedCargoOperations[0] : undefined;

    if (!cargoOperation) {
        if (logUndoStowage) console.log('Step 1: No cargo operations found to undo');
        return stowagePlanChanges;
    }

    if (cargoOperation.operationType === 'Unloading') {
        if (logUndoStowage) console.log('Step 1: Undoing unloading operation only');
        return stowagePlanChanges;
    }

    // Step 2: Find and remove stowage items for subsequent unloading operations
    const cargoOperationsAfter = getCargoOperationsAfter(stowagePlan, cargoOperation);

    logOperations(cargoOperationsAfter, 'Step 2: Found cargo operations after');

    const unloadingCargoOperationAfterGuids = cargoOperationsAfter
        .filter((op) => op.operationType === 'Unloading')
        .map((op) => op.cargoOperationGuid);
    if (logUndoStowage) console.log(`Step 2: Found ${unloadingCargoOperationAfterGuids.length} unloading operations after`);

    const subsequentResult = filterStowageItemsAndCreateChanges(
        stowagePlan.stowageItems,
        stowagePlan.stowagePlanGuid,
        (item) => {
            const isUnloadingAfter = unloadingCargoOperationAfterGuids.includes(item.cargoOperation?.cargoOperationGuid);
            // If tankId is specified, only delete items in that specific tank
            const isSameTank = tankId !== undefined ? item.tankId === tankId : true;
            return isUnloadingAfter && isSameTank;
        },
        'Step 2: Deleting subsequent stowage item',
        (item) => ({
            stowageGuid: item.stowageGuid,
            cargoOperationGuid: item.cargoOperation?.cargoOperationGuid,
            tankId: item.tankId,
        })
    );

    stowagePlan.stowageItems = subsequentResult.filteredItems;
    stowagePlanChanges = stowagePlanChanges.concat(subsequentResult.changes);
    const deletedStowageItems = primaryResult.removedItems;

    // Step 3: Delete affected transfer operations
    const cargoTransferOperationsToDelete = stowagePlan.cargoOperations.filter((op) =>
        transferOperationFilterFn(op, cargoOperation.cargoGuid, deletedStowageItems)
    );
    logOperations(cargoTransferOperationsToDelete, 'Step 3: Deleting transfer operations');

    const deletedTransferOperationChanges = cargoTransferOperationsToDelete.map((op) =>
        createAndLogDeleteStowageChange(
            stowagePlan.stowagePlanGuid,
            op.cargoOperationGuid,
            StowageChangeDataType.RotationCargoOperation,
            'Step 3: Deleting transfer operation',
            { cargoOperationGuid: op.cargoOperationGuid }
        )
    );

    stowagePlan.cargoOperations = stowagePlan.cargoOperations.filter((op) => !cargoTransferOperationsToDelete.includes(op));
    stowagePlanChanges = stowagePlanChanges.concat(deletedTransferOperationChanges);

    // Step 4: Remove stowage items for the affected transfer operations
    const affectedTransferStowageItems = filterStowageItemsAndCreateChanges(
        stowagePlan.stowageItems,
        stowagePlan.stowagePlanGuid,
        (item) => cargoTransferOperationsToDelete.some((op) => op.cargoOperationGuid === item.cargoOperation?.cargoOperationGuid),
        'Step 4: Deleting transfer stowage item',
        (item) => ({
            stowageGuid: item.stowageGuid,
            cargoOperationGuid: item.cargoOperation?.cargoOperationGuid,
        })
    );

    stowagePlan.stowageItems = affectedTransferStowageItems.filteredItems;
    stowagePlanChanges = stowagePlanChanges.concat(affectedTransferStowageItems.changes);

    // Step 5: Clean up temperature adjustments
    const temperatureChanges = cleanupTemperatureAdjustments(stowagePlan);

    return stowagePlanChanges.concat(temperatureChanges);
};

/**
 * Undo stowage for a specific cargo operation
 */
export const undoStowageForCargoOperation = (stowagePlan: StowagePlan, cargoOperation: RotationCargoOperation): StowagePlanChange[] => {
    if (logUndoStowage)
        console.log('Undoing stowage for cargo operation', {
            stowagePlanId: stowagePlan.stowagePlanGuid,
            cargoOperationId: cargoOperation.cargoOperationGuid,
            operationType: cargoOperation.operationType,
        });

    return undoStowageImplementation(
        stowagePlan,
        // Primary filter - items related to this cargo operation
        (item) => item.cargoOperationGuid === cargoOperation.cargoOperationGuid,
        // Transfer operation filter
        (op, cargoGuid, deletedItems) => {
            return (
                op.operationType === 'Transfer' &&
                op.cargoGuid === cargoGuid &&
                stowagePlan.stowageItems.some(
                    (item) =>
                        op.cargoOperationGuid === item.cargoOperation.cargoOperationGuid &&
                        deletedItems.some((deletedItem) => deletedItem.tankId === item.tankId)
                )
            );
        },
        { cargoOperationId: cargoOperation.cargoOperationGuid },
        undefined // No tank constraint - operate on all tanks
    );
};

/**
 * Undo stowage for a specific cargo and tank
 */
export const undoStowageForCargoAndTank = (stowagePlan: StowagePlan, cargo: RotationCargo, tank: Tank): StowagePlanChange[] => {
    if (logUndoStowage)
        console.log('Undoing stowage for cargo and tank', {
            stowagePlanId: stowagePlan.stowagePlanGuid,
            cargoId: cargo.cargoGuid,
            tankId: tank.tankId,
        });

    // First, check if we're deleting an unloading operation
    const deletedStowageItems = stowagePlan.stowageItems.filter(
        (item) =>
            isLoadingOrUnloadingCargoOperation(item.cargoOperation) &&
            item.cargoOperation.cargoGuid === cargo.cargoGuid &&
            item.tankId === tank.tankId
    );

    // Get the cargo operation being deleted (if it's an unloading operation)
    const unloadingOperation = deletedStowageItems.find(
        item => item.cargoOperation?.operationType === 'Unloading'
    )?.cargoOperation;

    // Perform the standard undo operation
    const standardChanges = undoStowageImplementation(
        stowagePlan,
        // Primary filter - items related to this cargo and tank
        (item) =>
            isLoadingOrUnloadingCargoOperation(item.cargoOperation) &&
            item.cargoOperation.cargoGuid === cargo.cargoGuid &&
            item.tankId === tank.tankId,
        // Transfer operation filter
        (op, cargoGuid, deletedItems) => {
            return (
                op.operationType === 'Transfer' &&
                op.cargoGuid === cargoGuid &&
                stowagePlan.stowageItems.some(
                    (item) =>
                        op.cargoOperationGuid === item.cargoOperation.cargoOperationGuid &&
                        item.tankId === tank.tankId &&
                        deletedItems.some((deletedItem) => deletedItem.tankId === item.tankId)
                )
            );
        },
        { cargoId: cargo.cargoGuid, tankId: tank.tankId },
        tank.tankId // Constrain to this specific tank
    );

    // If we deleted an unloading operation, cascade delete subsequent unloading operations
    let cascadeChanges: StowagePlanChange[] = [];
    if (unloadingOperation) {
        if (logUndoStowage)
            console.log('Cascade deleting subsequent unloading operations for tank', {
                tankId: tank.tankId,
                cargoOperationGuid: unloadingOperation.cargoOperationGuid
            });

        cascadeChanges = removeSubsequentUnloadingStowageItems(
            stowagePlan,
            unloadingOperation,
            [tank]
        );
    }

    return standardChanges.concat(cascadeChanges);
};

/**
 * Undo stowage for a specific cargo and multiple tanks
 * This function processes all tanks and collects all changes before returning
 */
export const undoStowageForCargoAndTanks = (stowagePlan: StowagePlan, cargo: RotationCargo, tanks: Tank[]): StowagePlanChange[] => {
    if (logUndoStowage)
        console.log('Undoing stowage for cargo and multiple tanks', {
            stowagePlanId: stowagePlan.stowagePlanGuid,
            cargoId: cargo.cargoGuid,
            tankCount: tanks.length,
            tankIds: tanks.map(t => t.tankId),
        });

    let allChanges: StowagePlanChange[] = [];

    // Process each tank
    tanks.forEach(tank => {
        const changes = undoStowageForCargoAndTank(stowagePlan, cargo, tank);
        allChanges = allChanges.concat(changes);
    });

    if (logUndoStowage) console.log(`Total changes for ${tanks.length} tanks: ${allChanges.length}`);

    return allChanges;
};

/**
 * Undo stowage for unloading cargo operations and a specific tank
 */
export const undoStowageForOtherUnloadingCargoOperationsAndTank = (
    stowagePlan: StowagePlan,
    cargoOperation: RotationCargoOperation,
    tank: Tank
): StowagePlanChange[] => {
    if (logUndoStowage)
        console.log('Undoing stowage for unloading cargo operations and tank', {
            stowagePlanId: stowagePlan.stowagePlanGuid,
            cargoId: cargoOperation.cargoGuid,
            tankId: tank.tankId,
        });

    return undoStowageImplementation(
        stowagePlan,
        // Primary filter - items related to this cargo and tank
        (item) =>
            item.cargoOperation.operationType === 'Unloading' &&
            item.cargoOperation.cargoOperationGuid !== cargoOperation.cargoOperationGuid &&
            item.cargoOperation.cargoGuid === cargoOperation.cargoGuid &&
            item.tankId === tank.tankId,
        // Transfer operation filter
        (op, cargoGuid, deletedItems) => {
            return (
                op.operationType === 'Transfer' &&
                op.cargoGuid === cargoGuid &&
                stowagePlan.stowageItems.some(
                    (item) =>
                        op.cargoOperationGuid === item.cargoOperation.cargoOperationGuid &&
                        item.tankId === tank.tankId &&
                        deletedItems.some((deletedItem) => deletedItem.tankId === item.tankId)
                )
            );
        },
        { cargoId: cargoOperation.cargoGuid, tankId: tank.tankId },
        tank.tankId // Constrain to this specific tank
    );
};
